import {
  BoxGeometry,
  Group,
  IcosahedronGeometry,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  PointLight,
  TorusGeometry,
  Vector3,
} from 'three';

const scratchAxis = new Vector3();

/** A hovering drone: a faceted shell around a glowing core. */
export class DroneView {
  readonly group = new Group();
  private readonly core: Mesh;
  private readonly ring: Mesh;
  private readonly net: Mesh;
  private readonly ringMaterial: MeshStandardMaterial;
  private readonly coreMaterial: MeshBasicMaterial;
  private readonly light: PointLight;

  constructor() {
    const shellMaterial = new MeshStandardMaterial({
      color: 0x8a2f3c,
      roughness: 0.45,
      metalness: 0.7,
      emissive: 0x3a0d14,
      emissiveIntensity: 0.5,
    });
    const shell = new Mesh(new IcosahedronGeometry(0.38, 0), shellMaterial);
    this.group.add(shell);

    this.coreMaterial = new MeshBasicMaterial({ color: 0xff5a4a });
    this.core = new Mesh(new IcosahedronGeometry(0.17, 0), this.coreMaterial);
    this.group.add(this.core);

    this.ringMaterial = new MeshStandardMaterial({
      color: 0xffb27a,
      emissive: 0xff6a3a,
      emissiveIntensity: 0.8,
      roughness: 0.4,
      metalness: 0.5,
    });
    this.ring = new Mesh(new TorusGeometry(0.5, 0.05, 6, 20), this.ringMaterial);
    this.ring.rotation.x = Math.PI / 2;
    this.group.add(this.ring);

    this.net = new Mesh(
      new IcosahedronGeometry(0.62, 1),
      new MeshBasicMaterial({
        color: 0x63f0d0,
        wireframe: true,
        transparent: true,
        opacity: 0.7,
      }),
    );
    this.net.visible = false;
    this.group.add(this.net);

    this.light = new PointLight(0xff6a4a, 1.2, 6);
    this.group.add(this.light);
  }

  update(position: Vector3, time: number, alerted = false, netted = false): void {
    this.group.position.copy(position);
    this.group.rotation.y = time * 0.8;
    this.ring.rotation.z = time * 1.6;
    this.core.scale.setScalar(1 + Math.sin(time * 6) * 0.12);
    this.light.intensity = 1 + Math.sin(time * 6) * 0.4;

    this.ringMaterial.color.setHex(alerted ? 0xffd36a : 0xffb27a);
    this.ringMaterial.emissive.setHex(alerted ? 0xffa02a : 0xff6a3a);
    this.light.color.setHex(netted ? 0x63f0d0 : alerted ? 0xffb24a : 0xff6a4a);
    this.coreMaterial.color.setHex(netted ? 0x63f0d0 : 0xff5a4a);
    this.net.visible = netted;
    if (netted) this.net.rotation.y = time * 1.8;
  }

  dispose(): void {
    this.group.traverse((child) => {
      if (child instanceof Mesh) {
        child.geometry.dispose();
        const material = child.material;
        if (Array.isArray(material)) material.forEach((m) => m.dispose());
        else material.dispose();
      }
    });
  }
}

/** A small crate used to dress the chamber. */
export function createCrate(size = 0.6): Mesh {
  const mesh = new Mesh(
    new BoxGeometry(size, size, size),
    new MeshStandardMaterial({ color: 0x39414f, roughness: 0.8, metalness: 0.3 }),
  );
  return mesh;
}

/** Emissive strip used to mark the edges of the chamber. */
export function createStrip(length: number, color = 0x63f0d0): Mesh {
  const mesh = new Mesh(new BoxGeometry(length, 0.06, 0.06), new MeshBasicMaterial({ color }));
  return mesh;
}

export { scratchAxis };
